/**
 * @file cursor.tsx
 * @summary
 * Componentes para criar um cursor personalizado na UI.
 *
 * @remarks
 * - Projetados para colaboração em tempo real.
 */

"use client";

import { cn } from "visuais/fmt";
import { forwardRef, Children } from "react";
import { CursorHTMLProps, CursorSVGProps } from "./tipos";

/**
 * Contêiner principal para um cursor personalizado.
 * @param {CursorHTMLProps} props - Configurações do cursor.
 * @param {React.Ref<HTMLDivElement>} ref - Referência ao elemento.
 * @returns {JSX.Element} Elemento `span` base do cursor.
 * @example
 * ```tsx
 * <Cursor style={{ transform: 'translate(100px, 50px)' }}>
 *   <CursorPointer />
 *   <CursorBody><CursorName>João</CursorName><CursorMessage>Olá!</CursorMessage></CursorBody>
 * </Cursor>
 */
export const Cursor = forwardRef<HTMLDivElement, CursorHTMLProps>(
  ({ className, children, ...props }, ref) => (
    <span
      className={cn("pointer-events-none relative select-none", className)}
      ref={ref}
      {...props}
    >
      {children}
    </span>
  )
);
Cursor.displayName = "Cursor";

/**
 * Ícone SVG do ponteiro do cursor.
 * @param {CursorSVGProps} props - Configurações do ícone.
 * @returns {JSX.Element} Elemento `svg` do ponteiro.
 * @example
 * ```tsx
 * <CursorPointer className="text-blue-500" />
 */
export const CursorPointer = ({ className, ...props }: CursorSVGProps) => (
  <svg
    aria-hidden="true"
    focusable="false"
    className={cn("size-3.5", className)}
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    fill="none"
    viewBox="0 0 20 20"
    {...props}
  >
    <path
      fill="currentColor"
      stroke="hsl(var(--foreground))"
      strokeWidth="1.5"
      d="M19.438 6.716 1.115.05A.832.832 0 0 0 .05 1.116L6.712 19.45a.834.834 0 0 0 1.557.025l3.198-8 7.995-3.2a.833.833 0 0 0 0-1.559h-.024Z"
    />
  </svg>
);
CursorPointer.displayName = "CursorPointer";

/**
 * Contêiner para nome e mensagem do cursor.
 * @param {CursorHTMLProps} props - Configurações do corpo.
 * @returns {JSX.Element} Elemento `span` do corpo.
 * @example
 * ```tsx
 * <CursorBody><CursorName>Alice</CursorName><CursorMessage>Digitando...</CursorMessage></CursorBody>
 */
export const CursorBody = ({
  children,
  className,
  ...props
}: CursorHTMLProps) => (
  <span
    className={cn(
      "relative ml-3.5 flex flex-col whitespace-nowrap rounded-xl py-1 pr-3 pl-2.5 text-xs",
      Children.count(children) > 1 && "rounded-tl",
      "bg-secondary text-foreground",
      className
    )}
    {...props}
  >
    {children}
  </span>
);

/**
 * Exibe o nome do usuário associado ao cursor.
 * @param {CursorHTMLProps} props - Configurações do nome.
 * @returns {JSX.Element} Elemento `span` do nome.
 * @example
 * ```tsx
 * <CursorName style={{ color: 'blue' }}>Bob</CursorName>
 */
export const CursorName = (props: CursorHTMLProps) => <span {...props} />;

/**
 * Exibe a mensagem ou status do cursor.
 * @param {CursorHTMLProps} props - Configurações da mensagem.
 * @returns {JSX.Element} Elemento `span` da mensagem.
 * @example
 * ```tsx
 * <CursorMessage>Conectado</CursorMessage>
 */
export const CursorMessage = (props: CursorHTMLProps) => <span {...props} />;
